import { resourceValidationIds } from './resource-validation-ids';
import { parseMaipoPublicScene, sceneSupportDiagnostics, verifyPublicAssets } from '@maipo/format';
import { walkLayers, type EditorDocument, type TextLayer, type Layer } from '../core/model';
import { throwIfAborted } from '../codecs/types';
import { documentPublicResources } from './adapter';

interface BoundFace { family: string; weight: number; face: FontFace; bytes: Uint8Array<ArrayBuffer> }
interface PreparedFonts { fonts: Map<string, BoundFace>; fontSet: FontFaceSet }
const prepared = new WeakMap<EditorDocument, PreparedFonts>();
let nextScope = 0;
const renderSources = new WeakMap<EditorDocument, EditorDocument>();

/** Temporary export projections borrow their live source's faces, never ownership. */
export function inheritNativeDocumentFonts(source: EditorDocument, projection: EditorDocument): void {
  renderSources.set(projection, source);
}

export function layerHasPublicFontReference(layer: Layer): boolean {
  return layer.kind === 'group' ? layer.children.some(layerHasPublicFontReference)
    : layer.kind === 'text' && layer.extensions['maipo:public-font-ref'] !== undefined;
}

export function releaseNativeDocumentFonts(document: EditorDocument): void {
  const previous = prepared.get(document);
  if (!previous) return;
  for (const { face } of previous.fonts.values()) previous.fontSet.delete(face);
  prepared.delete(document);
}

/** Opaque loaded state retained by undo commands; inactive faces are not registered. */
export type NativeFontState = PreparedFonts | undefined;
export function captureNativeFontState(document: EditorDocument): NativeFontState {
  return prepared.get(document);
}
export function activateNativeFontState(document: EditorDocument, state: NativeFontState): void {
  const previous = prepared.get(document);
  if (previous === state) return;
  const previousFaces = new Set([...previous?.fonts.values() ?? []].map(item => item.face));
  const nextFaces = new Set([...state?.fonts.values() ?? []].map(item => item.face));
  const added: FontFace[] = [];
  try {
    for (const face of nextFaces) {
      if (previousFaces.has(face)) continue;
      state!.fontSet.add(face); added.push(face);
    }
  } catch (error) {
    for (const face of added) state!.fontSet.delete(face);
    throw error;
  }
  for (const face of previousFaces) if (!nextFaces.has(face)) previous!.fontSet.delete(face);
  if (state) prepared.set(document, state);
  else prepared.delete(document);
}

/** Validate retained resources and activate document-private faces without changing authored styles. */
export async function prepareNativeDocumentFonts(document: EditorDocument, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal);
  const metadata = document.extensions['maipo:public-document'];
  // Ordinary native projects do not have to satisfy public-scene ID/name rules.
  if (metadata === undefined && !walkLayers(document.root).some(layer => layer.kind === 'text' && layer.extensions['maipo:public-font-ref'] !== undefined)) {
    releaseNativeDocumentFonts(document);
    return;
  }
  if (metadata !== undefined && (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)
    || (metadata as Record<string, unknown>).format !== 'maipo-scene'
    || (metadata as Record<string, unknown>).version !== '0.1')) {
    throw new Error('Unsupported embedded public document metadata.');
  }
  const declarations = metadata ? (metadata as Record<string, unknown>).extensionDeclarations : undefined;
  const resources = documentPublicResources(document);
  const validationId = resourceValidationIds(resources.fonts, resources.assets);
  const scene = parseMaipoPublicScene({
    format: 'maipo-scene', version: '0.1', documentId: validationId(), revision: 0,
    name: 'Native resources',
    canvas: { width: 1, height: 1, colorSpace: 'srgb', background: { type: 'transparent' } },
    ...resources, children: [],
    ...(declarations === undefined ? {} : { extensionDeclarations: declarations }),
  });
  const unsupported = sceneSupportDiagnostics(scene);
  if (unsupported.length) throw new Error(unsupported.map(item => item.message).join(' '));
  const errors = (await verifyPublicAssets(scene)).filter(item => item.severity === 'error');
  if (errors.length) throw new Error(`Embedded document resources are invalid: ${errors.map(item => item.code).join(', ')}.`);
  throwIfAborted(signal);
  const fonts = new Map(scene.fonts.map(font => [font.id, font]));
  const assets = new Map(scene.assets.map(asset => [asset.id, asset]));
  const needed = new Set<string>();
  for (const layer of walkLayers(document.root)) {
    if (layer.kind !== 'text') continue;
    const ref = layer.extensions['maipo:public-font-ref'];
    if (ref === undefined) continue;
    if (typeof ref !== 'string' || !fonts.has(ref)) throw new Error('This document has an unresolved public font reference. Open its original public scene.');
    const font = fonts.get(ref)!;
    if (font.style !== 'normal') {
      if (font.family === layer.text.style.family && font.weight === layer.text.style.weight) throw new Error(`Embedded font style ${font.style} is not supported by native text.`);
      continue;
    }
    // Keep the referenced face available after family edits or their undo. The
    // renderer still selects it only when authored family and weight match.
    if (font.source.kind === 'embedded') needed.add(ref);
  }
  if (!needed.size) { releaseNativeDocumentFonts(document); return; }
  if (typeof FontFace === 'undefined' || !globalThis.document?.fonts) throw new Error('Embedded document fonts require the browser font API.');
  const fontSet = globalThis.document.fonts;
  const loaded = new Map<string, BoundFace>();
  const scope = ++nextScope;
  try {
    for (const ref of needed) {
      const font = fonts.get(ref)!;
      if (font.source.kind !== 'embedded') continue;
      const asset = assets.get(font.source.asset)!;
      const bytes = Uint8Array.from(atob(asset.data), character => character.charCodeAt(0));
      const alias = `MaipoDocumentFont_${scope}_${asset.digest.slice(7)}_${font.weight}`;
      const face = new FontFace(alias, bytes, { weight: String(font.weight), style: font.style });
      await face.load();
      throwIfAborted(signal);
      loaded.set(ref, { family: font.family, weight: font.weight, face, bytes });
    }
    for (const { face } of loaded.values()) fontSet.add(face);
    releaseNativeDocumentFonts(document);
    prepared.set(document, { fonts: loaded, fontSet });
  } catch (error) {
    for (const { face } of loaded.values()) fontSet.delete(face);
    throw error;
  }
}

export function nativeTextRenderFamily(document: EditorDocument | undefined, layer: TextLayer): string {
  const ref = layer.extensions['maipo:public-font-ref'];
  const bound = document && typeof ref === 'string' ? prepared.get(renderSources.get(document) ?? document)?.fonts.get(ref) : undefined;
  return bound?.family === layer.text.style.family && bound.weight === layer.text.style.weight
    ? bound.face.family : layer.text.style.family;
}

export function nativeDocumentFontBytes(document: EditorDocument, layer: TextLayer): ArrayBuffer | undefined {
  const ref = layer.extensions['maipo:public-font-ref'];
  const bound = typeof ref === 'string' ? prepared.get(document)?.fonts.get(ref) : undefined;
  return bound?.family === layer.text.style.family && bound.weight === layer.text.style.weight
    ? bound.bytes.slice().buffer : undefined;
}
